import Image from 'next/image'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {getContactContent} from '@/content/contact'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {trackClickOnContactEmail} from '@/utils/tracking/helpers'

import {Text, TextVariant} from '../content/Text'

type ContactUsBannerProps = {
  title?: string
  description?: string
  trackedEventParameters: Record<string, string>
}

export const ContactUsBanner: FC<ContactUsBannerProps> = ({
  title,
  description,
  trackedEventParameters,
}) => {
  const {t} = useTranslation()
  const {emailAddress, emailAddressHref, contactBanner} = getContactContent()

  return (
    <>
      <div className="wrapper">
        <div className="image">
          <Image
            src={contactBanner.imageSrc}
            alt={contactBanner.imageAlt}
            style={{objectFit: 'contain'}}
            fill
            sizes={`${MOBILE_SCREENSIZE} 150px, 280px`}
          />
        </div>
        <div className="content">
          <Text variant={TextVariant.h4} element="h2">
            {title || t(contactBanner.title)}
          </Text>
          <div className="description">
            <Text color={theme.palette.text.light}>
              {description || t(contactBanner.description)}
            </Text>
          </div>
          <div className="button-wrapper">
            <Link
              href={emailAddressHref}
              onClick={() => trackClickOnContactEmail(trackedEventParameters)}
            >
              <Button variant={ButtonVariant.secondary} size={ButtonSize.small} type="button">
                {emailAddress}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
          align-items: center;
          padding-top: ${theme.spacing[6]};
          padding-bottom: ${theme.spacing[6]};
          padding-left: ${theme.spacing[8]};
          padding-right: ${theme.spacing[8]};
          background-color: ${theme.palette.background.butter};
          border-radius: 16px;
        }

        .image {
          position: relative;
          width: 280px;
          height: 150px;
          margin-right: ${theme.spacing[8]};
        }

        .content {
          flex: 1;
        }

        .description {
          margin-top: ${theme.spacing[2]};
        }

        .button-wrapper {
          margin-top: ${theme.spacing[3]};
        }

        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            padding-left: ${theme.spacing[6]};
            padding-right: ${theme.spacing[6]};
          }

          .image {
            margin-right: ${theme.spacing[6]};
          }

          .content {
            flex: 1 1 400px;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            flex-direction: column;
            padding-left: ${theme.spacing[4]};
            padding-right: ${theme.spacing[4]};
          }

          .image {
            width: 150px;
            height: 80px;
            margin-right: 0;
            margin-bottom: ${theme.spacing[4]};
          }

          .content {
            flex: 1;
            text-align: center;
          }
        }
      `}</style>
    </>
  )
}
